// PlaybackOptionsPanel.tsx
import React, { useState, useEffect } from "react";
import { InstrumentName } from "../types/sound";
import { useSoundLibrary } from "../hooks/useSoundLibrary";
import styles from "../styles/PlaybackOptions.module.css";
import InstrumentSelector from "./InstrumentSelector";
import Tooltip from "./Tooltip";

const allInstruments = [
    "cello",
    "e-piano",
    "harp",
    "piano",
    "saw-synth",
    "sine-synth",
    "square-synth",
    "viola",
] as InstrumentName[];

export interface PlaybackOptions {
    instrument: InstrumentName;
    volume: number; // 0–100
    muted: boolean;
    playMode: "melodic" | "harmonic";
}

interface PlaybackOptionsPanelProps {
    onChange: (options: PlaybackOptions) => void;
    initialOptions?: Partial<PlaybackOptions>;
    availableInstruments?: InstrumentName[];
    // when provided, hide the play mode controls and force this value
    forcePlayMode?: "melodic" | "harmonic";
}

export default function PlaybackOptionsPanel({
    onChange,
    initialOptions,
    availableInstruments = allInstruments, // add your supported instruments
    forcePlayMode,
}: PlaybackOptionsPanelProps) {
    const { loadInstrument, soundLibrary } = useSoundLibrary();

    const [instrument, setInstrument] = useState<InstrumentName>(
        (initialOptions?.instrument as InstrumentName) || "piano"
    );
    const [volume, setVolume] = useState(initialOptions?.volume ?? 80);
    const [muted] = useState(initialOptions?.muted ?? false);
    const [playMode, setPlayMode] = useState<"melodic" | "harmonic">(
        (forcePlayMode as "melodic" | "harmonic") || initialOptions?.playMode || "melodic"
    );

    // load chosen instrument
    useEffect(() => {
        loadInstrument(instrument);
    }, [instrument, loadInstrument]);

    // update parent whenever options change
    useEffect(() => {
        onChange({ instrument, volume, muted, playMode });
    }, [instrument, volume, muted, playMode, onChange]);

    // apply volume/mute globally if supported
    useEffect(() => {
        // if (soundLibrary?.setVolume) {
        //   soundLibrary.setVolume(muted ? -Infinity : volume); // Tone.js uses dB
        // }
    }, [volume, muted, soundLibrary]);

    return (
        <div className={styles.playbackOptionsWrapper}>
            <h4>Playback Options</h4>

            {/* Instrument Select */}
            <InstrumentSelector
                value={instrument}
                onChange={setInstrument}
                availableInstruments={availableInstruments}
            />

            {/* Volume */}
            <div className={styles.volumeWrapper}>
                <label className={styles.volumeLabelWrapper}>
                    Volume: {muted ? "Muted" : `${volume}%`}
                    <input
                        type="range"
                        min={0}
                        max={100}
                        value={muted ? 0 : volume}
                        onChange={(e) => setVolume(Number(e.target.value))}
                        disabled={muted}
                    />
                </label>
            </div>

            {/* Mute */}
            {/* <div>
                <label>
                    <input
                        type="checkbox"
                        checked={muted}
                        onChange={(e) => setMuted(e.target.checked)}
                    />
                    Mute
                </label>
            </div> */}

            {/* Play Mode - hide controls if forcePlayMode is provided */}
            {!forcePlayMode && (
                <div className={styles.playModeSection}>
                    <div className={styles.playModeHeader}>
                        <span className={styles.playModeLabel}>Play Mode</span>
                        <Tooltip
                            content={
                                <div>
                                    <strong>Melodic:</strong> Notes play one after another, like a melody.
                                    <br /><br />
                                    <strong>Harmonic:</strong> Notes play together at the same time, like a chord.
                                </div>
                            }
                            position="left"
                        >
                            <span className={styles.helpIcon}>?</span>
                        </Tooltip>
                    </div>
                    <div className={styles.playModeWrapper}>
                        <label
                            className={
                                playMode !== "melodic"
                                    ? styles.radioLabelWrapper
                                    : `${styles.radioLabelWrapper} ${styles.radioLabelWrapperChecked}`
                            }
                        >
                            <input
                                type="radio"
                                name="playMode"
                                value="melodic"
                                checked={playMode === "melodic"}
                                onChange={() => setPlayMode("melodic")}
                                className={styles.hiddenRadio}
                            />
                            Melodic
                        </label>
                        <label
                            className={
                                playMode !== "harmonic"
                                    ? styles.radioLabelWrapper
                                    : `${styles.radioLabelWrapper} ${styles.radioLabelWrapperChecked}`
                            }
                        >
                            <input
                                type="radio"
                                name="playMode"
                                value="harmonic"
                                checked={playMode === "harmonic"}
                                onChange={() => setPlayMode("harmonic")}
                                className={styles.hiddenRadio}
                            />
                            Harmonic
                        </label>
                    </div>
                </div>
            )}
        </div>
    );
}
